import { FC, ReactNode, useState } from "react";

import { LOGO_HEADER_SIZE, MOBILE_OVERLAY_ROUTE_QUERY_PARAM } from "components/constants";
import { BugReportModal } from "components/header/BugReportModal";
import { ConnectionStatusIndicator } from "components/header/ConnectionStatusIndicator";
import { RecordingStatusIndicator } from "components/header/RecordingStatusIndicator";
import { SectionSelect } from "components/header/SectionSelect";
import { useIntercomWithDefaults } from "components/intercom/use-intercom-with-defaults";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { SIDEBAR_PANEL_ICONS } from "helpers/sidebar";
import { t } from "i18n/i18n";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { ListItem } from "mds/components/ListItem";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  ChatStatusIcon,
  CircleQuestionIcon,
  CrossRemoveIcon,
  ForumLogoIcon,
  HelpBugIcon,
  HomeHouseIcon,
  TextBooksIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentClassUserIsInPersonClass,
  selectCurrentSectionId,
  selectQueryString,
} from "store/selectors";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { logoutAndClearCaches } from "utils/auth";
import { useAv } from "utils/av";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { usePresentation } from "utils/presentation";
import { useQueryParamsManager } from "utils/routing";
import {
  INSTRUCTOR_FORUM_USER_GUIDE_URL,
  STUDENT_FORUM_USER_GUIDE_URL,
  sectionHomeUrl,
  useViewLevel,
} from "utils/urls";
import { isIntercomEnabled } from "utils/waffle-flags";

type MenuListItemProps = {
  icon: ReactNode;
  text: string;
  overlayRoute: string | null;
  baseUrl?: string;
  onClick?: () => void;
};

const MenuListItem: FC<MenuListItemProps> = ({ icon, text, overlayRoute, baseUrl, onClick }) => {
  const queryString = useAppSelector(selectQueryString);

  // Generates a link that sets `overlay=<route>` in the URL query params,
  // but otherwise preserves the current URL. Returns the new URL.
  const mobileOverlayRouteLink = (route: string | null): string | undefined => {
    if (!route && !baseUrl) return undefined;
    // We don't want to change the object returned from the selector so we clone it.
    const newParams = new URLSearchParams(queryString);
    if (route) {
      newParams.set(MOBILE_OVERLAY_ROUTE_QUERY_PARAM, route);
    } else {
      newParams.delete(MOBILE_OVERLAY_ROUTE_QUERY_PARAM);
    }
    return `${baseUrl || window.location.pathname}?${newParams.toString()}`;
  };

  return (
    <ListItem
      className="h-[60px] group-data-[dark]/themable:border-black-tint-30 group-data-[dark]/themable:text-white group-data-[dark]/themable:hover:bg-black-tint-30"
      kind="primary"
      to={mobileOverlayRouteLink(overlayRoute)}
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        {icon}
        <span className="font-medium">{text}</span>
      </div>
    </ListItem>
  );
};

export const MobileMainMenu: FC = () => {
  const dispatch = useAppDispatch();
  const viewLevel = useViewLevel();
  const { deleteQueryParams } = useQueryParamsManager();
  const homeUrl = useHomeUrl();

  const { canAuthorSection } = useCapabilities();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const { showIntercom } = useIntercomWithDefaults();

  const isAssessing = useIsAssessing();
  const { isPresenting } = usePresentation();
  const restrictSectionSelect = isPresenting || isAssessing;
  const disabledSectionSelectTooltipText = isPresenting
    ? t("main_header.disallowed_while_presenting")
    : t("main_header.disallowed_while_assessing");

  const { isInVirtualClass, isVirtualClassActive } = useAv();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isInVirtualClassOrObserving = isInVirtualClass || isObservingClass;
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);

  const [showBugReportModal, setShowBugReportModal] = useState(false);

  return (
    <div
      className="group/themable flex h-dvh flex-col bg-white data-[dark]:bg-black-tint-15 data-[dark]:text-white"
      data-dark={isInVirtualClassOrObserving || undefined}
    >
      <div className="flex items-center justify-between border-b border-black-tint-90 px-4 py-2 group-data-[dark]/themable:border-black-tint-30">
        <div className="flex h-9 items-center">
          <ForumLogoIcon
            color={isInVirtualClassOrObserving ? "white" : "black"}
            width={LOGO_HEADER_SIZE}
          />
        </div>
        <SquareIconButton
          kind="ghost"
          size="s"
          title={t("common.close")}
          onClick={() => deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM])}
        >
          <CrossRemoveIcon />
        </SquareIconButton>
      </div>

      {/* 189px is the combined height of the top and bottom part of the menu */}
      <div className="page flex max-h-[calc(100dvh-189px)] flex-col">
        <RecordingStatusIndicator
          className="mb-4 border-b border-black-tint-30 pb-4 pl-3"
          isInMobileMenu
        />

        <SectionSelect
          disabled={restrictSectionSelect}
          disabledTooltipText={disabledSectionSelectTooltipText}
          openInNewTab={isInVirtualClassOrObserving || isInInPersonClass}
          isMobile
        />

        <div className="mt-4">
          {!viewLevel.home && (
            <MenuListItem
              baseUrl={homeUrl}
              icon={<HomeHouseIcon />}
              overlayRoute={null}
              text={t("main_header.home")}
            />
          )}

          {viewLevel.page && (
            <MenuListItem
              baseUrl={sectionHomeUrl(currentSectionId)}
              icon={<TextBooksIcon />}
              overlayRoute={null}
              text={t("main_header.back_to_section")}
            />
          )}

          {currentSectionId && (
            <MenuListItem
              icon={<SIDEBAR_PANEL_ICONS.Planner />}
              overlayRoute="planner"
              text={t("content_sidebar.title.Planner")}
            />
          )}

          <MenuListItem
            icon={<SIDEBAR_PANEL_ICONS.AiAssistant />}
            overlayRoute="ai"
            text={t("content_sidebar.title.AiAssistant")}
          />

          {currentSectionId && (
            <MenuListItem
              icon={<SIDEBAR_PANEL_ICONS.Users />}
              overlayRoute="users"
              text={t("content_sidebar.title.Users")}
            />
          )}

          <MenuListItem
            icon={<SIDEBAR_PANEL_ICONS.Profile />}
            overlayRoute="profile"
            text={t("content_sidebar.title.Profile")}
          />

          <MenuListItem
            icon={<HelpBugIcon />}
            overlayRoute={null}
            text={t("help_center.report_problem")}
            onClick={() => setShowBugReportModal(true)}
          />

          <MenuListItem
            icon={<CircleQuestionIcon />}
            overlayRoute={null}
            text={t("help_center.forum_user_guide")}
            onClick={() =>
              window.open(
                canAuthorSection ? INSTRUCTOR_FORUM_USER_GUIDE_URL : STUDENT_FORUM_USER_GUIDE_URL,
                "_blank",
                "noopener",
              )
            }
          />

          {isVirtualClassActive && (
            <MenuListItem
              icon={<SIDEBAR_PANEL_ICONS.ClassChat />}
              overlayRoute="chat"
              text={t("content_sidebar.title.ClassChat")}
            />
          )}

          {isIntercomEnabled() && (
            <MenuListItem
              icon={<ChatStatusIcon statusColor="green" />}
              overlayRoute={null}
              text={t("help_center.contact_support")}
              onClick={() => showIntercom()}
            />
          )}
        </div>

        <HorizontalDivider className="group-data-[dark]/themable:border-black-tint-30" />
      </div>

      {/* TODO: Lots of mobile buttons are full-width, we should have a prop for that */}
      <div className="flex flex-col items-center gap-2 px-3 pt-4">
        <ConnectionStatusIndicator
          className="text-black-tint-40 group-data-[dark]/themable:text-black-tint-55"
          iconColorClass={isInVirtualClassOrObserving ? "text-black-tint-55" : "text-black-tint-40"}
          showText
        />
        <SquareButton
          className="my-4 w-full !max-w-full"
          eventName="Mobile menu - logout"
          kind="tertiary"
          size="m"
          onClick={() => {
            logoutAndClearCaches(dispatch);
          }}
        >
          {t("profile_panel.logout")}
        </SquareButton>
      </div>

      {showBugReportModal && <BugReportModal onClose={() => setShowBugReportModal(false)} />}
    </div>
  );
};
